import React, { useEffect } from 'react';

import cx from 'classnames';

import { S } from 'components/typography';

import styles from './UndoToast.scss';

const DEFAULT_DURATION_MS = 5000;

export type UndoToastProps = {
  /** Controls the slide-in/visible state. */
  isOpen: boolean;
  /** Toast message (already-translated string or node). */
  message: React.ReactNode;
  /** When provided together with `onAction`, renders an action button instead of "dismiss". */
  actionLabel?: string;
  /** Invoked when the action button is clicked. The toast closes afterward. */
  onAction?: () => void;
  /** Invoked when the toast should close (auto-dismiss, dismiss button, or after an action). */
  onDismiss: () => void;
  /** Auto-dismiss window in ms. Defaults to 5000. */
  duration?: number;
};

/**
 * Feature-scoped toast for the inspection template library's hide/unhide flow.
 *
 * Intentionally NOT the shared `components/general/Toast.tsx` singleton: that
 * component is used by ~525 call sites, so this flow owns its own toast to add
 * an Undo action without touching shared code. Driven by props/state from
 * `InspectionsTemplateLibrary`, not a `window` global.
 */
export function UndoToast({
  isOpen,
  message,
  actionLabel,
  onAction,
  onDismiss,
  duration = DEFAULT_DURATION_MS,
}: UndoToastProps) {
  
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const timeoutId = setTimeout(() => onDismiss(), duration);

    return () => clearTimeout(timeoutId);
  }, [isOpen, message, duration, onDismiss]);

  const hasAction = Boolean(actionLabel && onAction);

  return (
    <div className={styles.container}>
      <div
        role="presentation"
        className={cx(styles.Toast, { [styles.visible]: isOpen })}
        data-test="toast"
      >
        <S>{message}</S>
        {hasAction ? (
          <button
            type="button"
            className={styles.actionButton}
            onClick={() => {
              onAction?.();
              onDismiss();
            }}
            data-test="toastAction"
          >
            <S>{actionLabel}</S>
          </button>
        ) : (
          <button
            type="button"
            className={styles.dismissButton}
            onClick={onDismiss}
          >
            <S>dismiss</S>
          </button>
        )}
      </div>
    </div>
  );
}

export default UndoToast;
